import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Plus,
  MapPin,
  Phone,
  EnvelopeSimple,
  Buildings,
  CurrencyDollar,
  Warning,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSiteDialogOpen, setIsSiteDialogOpen] = useState(false);
  const [newSite, setNewSite] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    delivery_instructions: '',
    access_notes: ''
  });

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await customersAPI.get(id);
      setCustomer(response.data);
    } catch (error) {
      toast.error('Failed to load customer');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = async () => {
    try {
      await customersAPI.addSite(id, newSite);
      toast.success('Site added successfully');
      setIsSiteDialogOpen(false);
      setNewSite({ name: '', address_line1: '', address_line2: '', city: '', postcode: '', delivery_instructions: '', access_notes: '' });
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to add site');
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Delete this site?')) return;
    try {
      await customersAPI.deleteSite(id, siteId);
      toast.success('Site deleted');
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  const handleUpdateCredit = async (creditData) => {
    try {
      await customersAPI.updateCredit(id, creditData);
      toast.success('Credit updated');
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to update credit');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/customers')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading font-bold">{customer.company_name}</h1>
            {customer.credit?.is_on_stop && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <Warning className="w-3 h-3" />
                Credit Stop
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground font-mono text-sm">{customer.account_number}</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sites">Sites ({customer.sites?.length || 0})</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="credit">Credit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Buildings className="w-5 h-5" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-muted-foreground">Company Name</Label>
                  <p className="font-medium">{customer.company_name}</p>
                </div>
                {customer.trading_name && (
                  <div>
                    <Label className="text-muted-foreground">Trading Name</Label>
                    <p>{customer.trading_name}</p>
                  </div>
                )}
                {customer.vat_number && (
                  <div>
                    <Label className="text-muted-foreground">VAT Number</Label>
                    <p className="font-mono">{customer.vat_number}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CurrencyDollar className="w-5 h-5" />
                  Credit Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Credit Limit</span>
                  <span className="font-mono font-bold">£{(customer.credit?.credit_limit || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Exposure</span>
                  <span className="font-mono">£{(customer.credit?.current_exposure || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Terms</span>
                  <span>{customer.credit?.payment_terms_days || 30} days</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Primary Site */}
          {customer.sites?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Primary Delivery Site
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const primarySite = customer.sites.find(s => s.is_primary) || customer.sites[0];
                  return (
                    <div>
                      <p className="font-medium">{primarySite.name}</p>
                      <p className="text-muted-foreground">{primarySite.address_line1}</p>
                      {primarySite.address_line2 && <p className="text-muted-foreground">{primarySite.address_line2}</p>}
                      <p className="text-muted-foreground">{primarySite.city}, {primarySite.postcode}</p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sites" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isSiteDialogOpen} onOpenChange={setIsSiteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="add-site-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Site
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Delivery Site</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Site Name *</Label>
                    <Input
                      value={newSite.name}
                      onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                      placeholder="Main Depot"
                      data-testid="site-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 1 *</Label>
                    <Input
                      value={newSite.address_line1}
                      onChange={(e) => setNewSite({ ...newSite, address_line1: e.target.value })}
                      placeholder="123 Industrial Road"
                      data-testid="site-address1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 2</Label>
                    <Input
                      value={newSite.address_line2}
                      onChange={(e) => setNewSite({ ...newSite, address_line2: e.target.value })}
                      placeholder="Industrial Estate"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={newSite.city}
                        onChange={(e) => setNewSite({ ...newSite, city: e.target.value })}
                        placeholder="Manchester"
                        data-testid="site-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Postcode *</Label>
                      <Input
                        value={newSite.postcode}
                        onChange={(e) => setNewSite({ ...newSite, postcode: e.target.value })}
                        placeholder="M1 1AA"
                        data-testid="site-postcode"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Delivery Instructions</Label>
                    <Textarea
                      value={newSite.delivery_instructions}
                      onChange={(e) => setNewSite({ ...newSite, delivery_instructions: e.target.value })}
                      placeholder="Enter through back gate..."
                    />
                  </div>
                  <Button onClick={handleAddSite} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="save-site-btn">
                    Add Site
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {customer.sites?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No sites added yet. Add a delivery site to place orders.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customer.sites.map((site) => (
                <Card key={site.id} className="hover:border-amber-500/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {site.name}
                          {site.is_primary && <Badge variant="secondary" className="text-xs">Primary</Badge>}
                        </CardTitle>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteSite(site.id)} className="text-red-600">
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p>{site.address_line1}</p>
                      {site.address_line2 && <p>{site.address_line2}</p>}
                      <p>{site.city}, {site.postcode}</p>
                      {site.delivery_instructions && (
                        <p className="text-muted-foreground text-xs mt-2 p-2 bg-muted rounded">
                          {site.delivery_instructions}
                        </p>
                      )}
                      {site.tanks?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {site.tanks.length} tank(s) registered
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pricing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pricing Profile</CardTitle>
              <CardDescription>Customer-specific pricing configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Base Price per Litre</Label>
                  <p className="font-mono text-lg">£{(customer.pricing?.base_price_per_litre || 0).toFixed(4)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Discount</Label>
                  <p className="font-mono text-lg">{customer.pricing?.discount_percentage || 0}%</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Contract Rate</Label>
                  <p className="font-mono text-lg">
                    {customer.pricing?.contract_rate ? `£${customer.pricing.contract_rate.toFixed(4)}` : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Price Band</Label>
                  <p>{customer.pricing?.price_band || 'Standard'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Credit Management</CardTitle>
              <CardDescription>Manage customer credit limits and terms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs">Credit Limit</Label>
                  <p className="font-mono text-xl font-bold">£{(customer.credit?.credit_limit || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs">Current Exposure</Label>
                  <p className="font-mono text-xl font-bold">£{(customer.credit?.current_exposure || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs">Available</Label>
                  <p className="font-mono text-xl font-bold text-emerald-600">
                    £{Math.max(0, (customer.credit?.credit_limit || 0) - (customer.credit?.current_exposure || 0)).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs">Payment Terms</Label>
                  <p className="text-xl font-bold">{customer.credit?.payment_terms_days || 30} days</p>
                </div>
              </div>

              {customer.credit?.is_on_stop && (
                <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <Warning className="w-5 h-5" />
                    <span className="font-bold">Account on Credit Stop</span>
                  </div>
                  {customer.credit?.stop_reason && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{customer.credit.stop_reason}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerDetailPage;
